import React, { Component } from 'react'
import Modal from './Modal/Modal'
import Cropper from 'cropperjs'
import { Row, Col, Icon, Upload, Button } from 'antd'
import { NotificationService } from '../../legacy_components/services/NotificationService'
import 'cropperjs/dist/cropper.css'
import './ImageUploader.css'

if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
    value: function (callback, type, quality) {
      const binStr = atob(this.toDataURL(type, quality).split(',')[1] )
      const len = binStr.length
      const arr = new Uint8Array(len)
      for (let i = 0; i < len; i += 1) {
        arr[i] = binStr.charCodeAt(i)
      }
      const blob = new Blob([arr], { type: type || 'image/jpeg' })
      callback(blob)
    }
  })
}

const { Dragger } = Upload

const acceptedImgMimeTypes = 'image/jpeg, .png, .gif'

const cropperSettings = {
  aspectRatio: 1,
  autoCropArea: 1,
  background: false,
  guides: false,
  minCropBoxWidth: 50,
  minCropBoxHeight: 50,
  movable: false,
  rotatable: false,
  scalable: false,
  zoomable: false,
}

const addJpgExtension = fileName => (fileName || '').replace(/\.(png|gif)/ig, '.jpg')

class ImageUploader extends Component {
  imgElement = null

  fileInputElement =  null

  cropperBodyElement = null

  cropper = null

  croppedImgDimensions = {}

  state = {
    imgData: null,
    error: '',
  }

  initImageCropping = () => {
    if (this.imgElement) {
      this.cropper = new Cropper(
        this.imgElement,
        { ...cropperSettings, crop: this.handleImgCrop },
      )
    }
  }

  resetImgCropper = () => {
    if (this.cropper) {
      this.cropper.destroy()
      this.initImageCropping()
    }
  }

  handleImgCrop = event => {
    const { x, y, width, height } = event.detail
    this.croppedImgDimensions = { x, y, width, height }
  }

  cropImage = () => {
    const { imgElement, croppedImgDimensions } = this
    const { x, y, width, height } = croppedImgDimensions

    const canvas = document.createElement('canvas')
    const canvasCtx = canvas.getContext('2d')
    const { naturalWidth, naturalHeight } = imgElement
    canvas.width = naturalWidth
    canvas.height = naturalHeight

    canvasCtx.drawImage(imgElement, 0, 0)
    const croppedImgData = canvasCtx.getImageData(x, y, width, height)

    return croppedImgData
  }

  getFileFromBlob = blob => {
    let file
    const fileName = addJpgExtension(this.fileName)
    try {
      file = new File([blob], fileName, { type: 'image/jpeg' })
    } catch (e) {
      file = blob
      console.log(e)
    }
    return file
  }

  getBlobOfImageData = imgData =>
    new Promise(resolve => {
      const { width, height } = this.croppedImgDimensions
      const canvas = document.createElement('canvas')
      const canvasCtx = canvas.getContext('2d')
      canvas.width = width
      canvas.height = height
      canvas.fillStyle = '#ffffff'

      canvasCtx.fillRect(0, 0, width, height)
      canvasCtx.putImageData(imgData, 0, 0)
      canvas.toBlob(
        blob => {
          blob.name = this.fileName
          blob.lastModifiedDate = Number(new Date())
          resolve(blob)
        },
        'image/jpeg',
        0.95,
      )
    })

  handleAddImgRef = imgElement => {
    if (imgElement) {
      this.imgElement = imgElement
      imgElement.onload = this.initImageCropping()
    }
  }

  handleImgUpload = async () => {
    const { onUpload, onHide } = this.props
    const croppedImgData = this.cropImage()
    
    const imgBlob = await this.getBlobOfImageData(croppedImgData)
    const imgFile = this.getFileFromBlob(imgBlob)

    onUpload(imgFile)
    onHide()

    this.reset()
  }

  reset = () => {
    this.setState({ error: '', imgData: null })
    if (this.cropper) {
      this.cropper.destroy()
    }
    if (this.fileInputElement) {
      this.fileInputElement.value = ''
    }
  }

  getImageDataFromFile = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onerror = reject
      reader.onload = () => {
        const imgData = reader.result
        resolve(imgData)
      }
      reader.readAsDataURL(file)
    })

  displayImage = file => {
    this.getImageDataFromFile(file).then(imgData => {
      this.setState({ imgData, error: '' })
      this.resetImgCropper()
    }).catch(err => {
      this.setState({
        error: 'Oops! We are unable to upload the image. Please try again',
      })
    })
  }

  hideModal = () => {
    this.props.onHide()
    this.reset()
  }

  handleBeforeUpload = file => {
    if (file) {
      if(this.checkFileSize(file)){
        this.displayImage(file)
        this.fileName = file.name
      }
    }

    return false
  }

  handleAddInputRef = fileInputElement => {
    if (fileInputElement) {
      this.fileInputElement = fileInputElement
    }
  }

  handleAddCropperBodyRef = cropperBodyElement => {
    if (cropperBodyElement) {
      this.cropperBodyElement = cropperBodyElement
    }
  }

  getImageMaxHeight = () =>
    this.cropperBodyElement
      ? `${this.cropperBodyElement.offsetHeight}px`
      : '500px'

  checkFileSize = (file) => {
    const { maxFileSizeInMB = 4 } = this.props;
    const hasGoodSize = file.size / 1024 / 1024 < maxFileSizeInMB;
    if (!hasGoodSize) {
      NotificationService.error(`File size too big, upload limited to ${maxFileSizeInMB}MB.`);
      return false;
    }
    return true;
  }

  handleInputChange = (event, x) => {
    const { target } = event
    const { files } = target
    const [file] = files || []

    if (file) {
      if(this.checkFileSize(file)){
        this.displayImage(file)
        this.fileName = file.name
      }
    }
  }

  handleOpenFileDialog = () => {
    if (this.fileInputElement) {
      this.fileInputElement.click()
    }
  }

  getFooterContent = () => {
    const uploadText = this.state.imgData
      ? 'Select another image'
      : 'Select an image from your computer'
    const buttons = [
      <Button key="re-upload" className="re-upload" onClick={this.handleOpenFileDialog}>
        {uploadText}
      </Button>,
    ]

    if (this.state.imgData) {
      buttons.push(
        <div className="image-uploader-modal-footer-buttons">
          <Button key="back" onClick={this.hideModal}>
            Cancel
          </Button>
          <Button key="submit" type="primary" onClick={this.handleImgUpload}>
            Upload
          </Button>
        </div>
      )
    } else {
      buttons.push(
        <div className="image-uploader-modal-footer-buttons">
          <Button key="back" onClick={this.hideModal}>
            Cancel
          </Button>
        </div>
      )
    }
    return buttons
  }

  render() {
    const { isVisible } = this.props
    const { imgData, error } = this.state

    return (
      <Modal
        title="Upload your avatar image"
        visible={isVisible}
        onOk={this.handleImgCrop}
        onCancel={this.hideModal}
        footer={this.getFooterContent()}
        wrapClassName="image-uploader-modal"
      >
        <div className="image-uploader-body" ref={this.handleAddCropperBodyRef}>
          {imgData ? (
            <div className="img-container">
              <div className="editor-container">
                <img
                  alt="Edit avatar"
                  ref={this.handleAddImgRef}
                  style={{ maxHeight: this.getImageMaxHeight() }}
                  src={imgData}
                />
              </div>
            </div>
          ) : (
            <Dragger
              accept={acceptedImgMimeTypes}
              beforeUpload={this.handleBeforeUpload}
              multiple={false}
              openFileDialogOnClick={false}
              size="small"
            >
              <Row type="flex" justify="center" align="middle">
                <Col span={18}>
                  <div className="image-uploader-background-text">
                    <Icon type="upload" />
                    <br />
                    Drag a profile photo here
                  </div>
                </Col>
              </Row>
            </Dragger>
          )}
        </div>
        <input
          accept={acceptedImgMimeTypes}
          ref={this.handleAddInputRef}
          style={{ position: 'absolute', left: '-90000px' }}
          type="file"
          onChange={this.handleInputChange}
        />
        {error && <div className="image-uploader-error">{error}</div>}
      </Modal>
    )
  }
}



export default ImageUploader
